import axios from 'axios';
import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import { useQuery } from 'react-query';

type Currency = 'USD' | 'JPY';

const loadETHPrice = async () => {
  const response = await axios.get<{
    USD: number;
    JPY: number;
  }>('https://min-api.cryptocompare.com/data/price?fsym=ETH&tsyms=USD,JPY');

  return response.data;
};

const useETHPrice = () => {
  const { i18n } = useTranslation();

  const { data, refetch } = useQuery({
    queryKey: ['ethPrice'],
    queryFn: loadETHPrice,
  });

  const ethToUSD = data?.USD || null;
  const ethToJPY = data?.JPY || null;

  const convertETHToCurrency = (
    ethPrice: string,
    currency: Currency
  ): string | 'loading' => {
    if (
      (currency === 'USD' && !ethToUSD) ||
      (currency === 'JPY' && !ethToJPY)
    ) {
      return 'loading';
    }

    // If currency is JPY, no decimal point is needed
    if (currency === 'JPY') {
      return `${Math.round(
        Number(formatEther(ethPrice)) * ethToJPY
      ).toLocaleString('ja-JP')}`; // add comma to separate thousands
    }

    // If currency is USD, convert to two decimal places
    return `${(
      Math.round(Number(formatEther(ethPrice)) * ethToUSD * 100) / 100
    ).toLocaleString('en-US')}`; // add comma to separate thousands
  };

  const convertETHToLocalCurrency = useCallback(
    (ethPrice: string) => {
      if (i18n.language === 'ja') {
        return `${convertETHToCurrency(ethPrice, 'JPY')} JPY`;
      }

      return `${convertETHToCurrency(ethPrice, 'USD')} USD`;
    },
    [i18n, convertETHToCurrency]
  );

  return {
    ethToUSD,
    ethToJPY,
    reload: () => refetch(),
    convertETHToCurrency,
    convertETHToLocalCurrency,
  };
};

export default useETHPrice;
